import { useEffect, useRef, useState } from 'react'
import type { UseUserResult } from '../../hooks/useUser'
import type { ActivityLevel, BackupInfo, Goal, PasswordMode, UserData } from '../../types'
import { computeTargets, profileComplete, ACTIVITY_LABELS, GOAL_LABELS } from '../../utils/targets'
import {
  setPassword as apiSetPassword,
  removePassword as apiRemovePassword,
  updateSettings,
  listBackups,
  getBackup,
  deleteUserData,
  ApiHttpError,
} from '../../utils/api'
import { ensureDataStructure } from '../../utils/storage'
import { showNotification } from '../../utils/notifications'
import Modal from '../layout/Modal'

export type SettingsSection = 'profile' | 'security' | 'data'

interface SettingsModalProps {
  section: SettingsSection | null
  onClose: () => void
  user: UseUserResult
  onLock?: () => void
}

const SECTION_TITLES: Record<SettingsSection, string> = {
  profile: 'Profile',
  security: 'Security',
  data: 'Backups & data',
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const apiError = (e: unknown, fallback: string): string =>
  e instanceof ApiHttpError ? e.message : fallback

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 border-t border-[var(--border)] pt-4 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-secondary)]">{title}</h3>
      {children}
    </section>
  )
}

export default function SettingsModal({ section, onClose, user, onLock }: SettingsModalProps) {
  const { data, updateData, getCachedPassword, cachePassword, reload } = user
  const userId = data.indexKey || ''
  const hasPassword = !!data.passwordHash

  // Profile
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [weight, setWeight] = useState('')
  const [gender, setGender] = useState<'' | 'male' | 'female'>('')
  const [heightCm, setHeightCm] = useState('')
  const [dob, setDob] = useState('')
  const [activityLevel, setActivityLevel] = useState<ActivityLevel | ''>('')
  const [goal, setGoal] = useState<Goal | ''>('')
  const [customCalories, setCustomCalories] = useState('')
  const [customProtein, setCustomProtein] = useState('')

  // Security
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [passwordMode, setPasswordMode] = useState<PasswordMode>('on_load')
  const [timeoutMinutes, setTimeoutMinutes] = useState('5')

  // Data
  const [backups, setBackups] = useState<BackupInfo[]>([])
  const [backupsLoaded, setBackupsLoaded] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!section) return
    setName(data.name ?? '')
    setEmail(data.email ?? data.backupEmail ?? '')
    setWeight(data.weight ? String(data.weight) : '')
    setGender(data.gender ?? '')
    setHeightCm(data.heightCm ? String(data.heightCm) : '')
    setDob(data.dob ?? '')
    setActivityLevel(data.activityLevel ?? '')
    setGoal(data.goal ?? '')
    setCustomCalories(data.customCalories ? String(data.customCalories) : '')
    setCustomProtein(data.customProtein ? String(data.customProtein) : '')
    setPasswordMode(data.passwordMode ?? 'on_load')
    setTimeoutMinutes(data.timeoutMinutes ? String(data.timeoutMinutes) : '5')
    setCurrentPassword(getCachedPassword() ?? '')
    setNewPassword('')
    setConfirmPassword('')
    setBackups([])
    setBackupsLoaded(false)
  }, [section, data, getCachedPassword])

  // ---- handlers ----------------------------------------------------------

  const saveProfile = async () => {
    updateData({
      name,
      email,
      weight: Number(weight) || 0,
      gender,
      heightCm: Number(heightCm) || undefined,
      dob: dob || undefined,
      activityLevel: activityLevel || undefined,
      goal: goal || undefined,
      customCalories: Number(customCalories) || undefined,
      customProtein: Number(customProtein) || undefined,
    })

    // The profile email doubles as the recovery email — keep the email→key index
    // in sync (PATCH /settings, password-gated). Skip if it's not a valid address.
    const e = email.trim()
    const stored = data.backupEmail ?? ''
    if (e !== stored && (e === '' || EMAIL_RE.test(e))) {
      try {
        await updateSettings(userId, {
          backupEmail: e,
          currentPassword: hasPassword ? getCachedPassword() ?? undefined : undefined,
        })
      } catch (err) {
        showNotification(apiError(err, 'Profile saved, but recovery email could not be updated'), 'error')
        onClose()
        return
      }
    } else if (e !== '' && !EMAIL_RE.test(e)) {
      showNotification("Profile saved — email looks invalid, so recovery wasn't updated", 'error')
      onClose()
      return
    }

    reload()
    showNotification('Profile saved')
    onClose()
  }

  const previewData: UserData = {
    ...data,
    weight: Number(weight) || 0,
    gender,
    heightCm: Number(heightCm) || undefined,
    dob: dob || undefined,
    activityLevel: activityLevel || undefined,
    goal: goal || undefined,
    customCalories: Number(customCalories) || undefined,
    customProtein: Number(customProtein) || undefined,
  }
  const preview = profileComplete(previewData) ? computeTargets(previewData) : null

  const savePassword = async () => {
    if (newPassword.length < 8) {
      showNotification('Password must be at least 8 characters', 'error')
      return
    }
    if (newPassword !== confirmPassword) {
      showNotification('Passwords do not match', 'error')
      return
    }
    try {
      await apiSetPassword(
        userId,
        newPassword,
        hasPassword ? currentPassword : undefined,
        passwordMode,
        passwordMode === 'after_timeout' ? Number(timeoutMinutes) || 5 : undefined,
      )
      cachePassword(newPassword)
      sessionStorage.setItem(`wlns:unlocked:${userId}`, '1')
      setNewPassword('')
      setConfirmPassword('')
      reload()
      showNotification(hasPassword ? 'Password changed' : 'Password set')
    } catch (e) {
      showNotification(apiError(e, 'Could not set password'), 'error')
    }
  }

  const removePw = async () => {
    if (!window.confirm('Remove password protection?')) return
    try {
      await apiRemovePassword(userId, currentPassword)
      reload()
      showNotification('Password removed')
    } catch (e) {
      showNotification(apiError(e, 'Could not remove password'), 'error')
    }
  }

  const saveLockSettings = async () => {
    try {
      await updateSettings(userId, {
        passwordMode,
        timeoutMinutes: passwordMode === 'after_timeout' ? Number(timeoutMinutes) || 5 : 0,
        currentPassword: hasPassword ? currentPassword : undefined,
      })
      reload()
      showNotification('Lock settings saved')
    } catch (e) {
      showNotification(apiError(e, 'Could not save lock settings'), 'error')
    }
  }

  const loadBackups = async () => {
    try {
      const res = await listBackups(userId)
      setBackups(res.backups || [])
      setBackupsLoaded(true)
    } catch (e) {
      showNotification(apiError(e, 'Could not load backups'), 'error')
    }
  }

  const restoreBackup = async (ts: string) => {
    if (!window.confirm('Restore this backup? Your current entries will be replaced.')) return
    try {
      const res = await getBackup(userId, ts)
      const v = res.data?.value
      if (!v) {
        showNotification('Backup not found', 'error')
        return
      }
      const restored = ensureDataStructure(v)
      updateData({
        name: restored.name,
        email: restored.email,
        weight: Number(restored.weight) || 0,
        gender: restored.gender,
        lists: restored.lists,
        intake: restored.intake ?? [],
      })
      showNotification('Backup restored')
      onClose()
    } catch (e) {
      showNotification(apiError(e, 'Could not restore backup'), 'error')
    }
  }

  const exportData = () => {
    const clean: Partial<UserData> = {
      indexKey: data.indexKey,
      name: data.name,
      email: data.email,
      weight: data.weight,
      gender: data.gender,
      lists: data.lists,
      intake: data.intake ?? [],
    }
    const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `wlns-${data.indexKey || 'export'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const importData = (file: File) => {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as UserData
        if (!parsed.lists) throw new Error('Missing lists')
        const safe = ensureDataStructure(parsed)
        if (!window.confirm('Import will replace your current entries. Continue?')) return
        updateData({
          name: safe.name,
          email: safe.email,
          weight: Number(safe.weight) || 0,
          gender: safe.gender,
          lists: safe.lists,
          intake: safe.intake ?? [],
        })
        showNotification('Data imported')
        onClose()
      } catch {
        showNotification('Invalid backup file', 'error')
      }
    }
    reader.readAsText(file)
  }

  const deleteAccount = async () => {
    if (!window.confirm('Permanently delete this log and all its data? This cannot be undone.')) return
    try {
      await deleteUserData(userId)
      window.location.href = '/'
    } catch (e) {
      showNotification(apiError(e, 'Could not delete'), 'error')
    }
  }

  const labelCls = 'block text-sm mb-1 text-[var(--text-secondary)]'

  return (
    <Modal isOpen={section !== null} onClose={onClose} title={section ? SECTION_TITLES[section] : ''} size="lg">
      {section === 'profile' && (
        <div className="space-y-5">
          <SubSection title="Your key">
            <div className="flex gap-2">
              <input className="flex-1" readOnly value={`${window.location.origin}/u/${userId}`} />
              <button
                className="btn btn-secondary"
                onClick={() =>
                  navigator.clipboard?.writeText(`${window.location.origin}/u/${userId}`).then(
                    () => showNotification('Link copied'),
                    () => showNotification('Could not copy', 'error'),
                  )
                }
              >
                Copy
              </button>
            </div>
          </SubSection>

          <SubSection title="About you">
            <div>
              <label className={labelCls}>Name (optional)</label>
              <input className="w-full" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input className="w-full" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              <p className="text-xs text-[var(--text-secondary)] mt-1">Used for account recovery if you lose your key.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Weight (kg)</label>
                <input className="w-full" type="number" inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Gender</label>
                <select className="w-full" value={gender} onChange={(e) => setGender(e.target.value as '' | 'male' | 'female')}>
                  <option value="">Select…</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Height (cm)</label>
                <input className="w-full" type="number" inputMode="decimal" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Date of birth</label>
                <input className="w-full" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Activity level</label>
              <select className="w-full" value={activityLevel} onChange={(e) => setActivityLevel(e.target.value as ActivityLevel | '')}>
                <option value="">Select…</option>
                {(Object.keys(ACTIVITY_LABELS) as ActivityLevel[]).map((k) => (
                  <option key={k} value={k}>{ACTIVITY_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Goal</label>
              <select className="w-full" value={goal} onChange={(e) => setGoal(e.target.value as Goal | '')}>
                <option value="">Select…</option>
                {(Object.keys(GOAL_LABELS) as Goal[]).map((k) => (
                  <option key={k} value={k}>{GOAL_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Calorie target (optional)</label>
                <input className="w-full" type="number" inputMode="decimal" placeholder="auto" value={customCalories} onChange={(e) => setCustomCalories(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Protein target g (optional)</label>
                <input className="w-full" type="number" inputMode="decimal" placeholder="auto" value={customProtein} onChange={(e) => setCustomProtein(e.target.value)} />
              </div>
            </div>

            {preview ? (
              <div className="card text-sm">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                  <div><div className="font-bold">{preview.bmi ? preview.bmi.toFixed(1) : '—'}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">BMI</div></div>
                  <div><div className="font-bold">{preview.bmr}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">BMR</div></div>
                  <div><div className="font-bold">{preview.tdee}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">TDEE</div></div>
                  <div><div className="font-bold text-[var(--accent-teal)]">{preview.calorieTarget}</div><div className="text-[10px] uppercase text-[var(--text-secondary)]">Target</div></div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">
                Fill in weight, height, date of birth, gender, activity level and goal to compute your BMR,
                TDEE and personalised nutrition targets.
              </p>
            )}

            <button className="btn btn-primary" onClick={saveProfile}>Save profile</button>
          </SubSection>
        </div>
      )}

      {section === 'security' && (
        <div className="space-y-4">
          {hasPassword && (
            <div>
              <label className={labelCls}>Current password</label>
              <input className="w-full" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{hasPassword ? 'New password' : 'Password'}</label>
              <input className="w-full" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Confirm</label>
              <input className="w-full" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Lock mode</label>
              <select className="w-full" value={passwordMode} onChange={(e) => setPasswordMode(e.target.value as PasswordMode)}>
                <option value="on_load">Lock on every visit</option>
                <option value="after_timeout">Lock after inactivity</option>
              </select>
            </div>
            {passwordMode === 'after_timeout' && (
              <div>
                <label className={labelCls}>Idle minutes</label>
                <input className="w-full" type="number" min={1} value={timeoutMinutes} onChange={(e) => setTimeoutMinutes(e.target.value)} />
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={savePassword}>
              {hasPassword ? 'Change password' : 'Set password'}
            </button>
            {hasPassword && (
              <>
                <button className="btn btn-secondary" onClick={saveLockSettings}>Save lock settings</button>
                {onLock && (
                  <button className="btn btn-secondary" onClick={onLock}>Lock now</button>
                )}
                <button className="btn btn-danger" onClick={removePw}>Remove password</button>
              </>
            )}
          </div>
          <p className="text-xs text-[var(--text-secondary)]">
            A password gates access to this log in the browser. It can't be reset automatically if forgotten.
          </p>
        </div>
      )}

      {section === 'data' && (
        <div className="space-y-5">
          <SubSection title="Backups">
            {!backupsLoaded ? (
              <button className="btn btn-secondary" onClick={loadBackups}>Load backups</button>
            ) : backups.length === 0 ? (
              <p className="text-sm text-[var(--text-secondary)]">No backups yet.</p>
            ) : (
              <ul className="space-y-2">
                {backups.map((b) => (
                  <li key={b.timestamp} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-[var(--text-secondary)]">{new Date(Number(b.timestamp)).toLocaleString()}</span>
                    <button className="btn btn-secondary" onClick={() => restoreBackup(b.timestamp)}>Restore</button>
                  </li>
                ))}
              </ul>
            )}
          </SubSection>

          <SubSection title="Export / import">
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-secondary" onClick={exportData}>Export JSON</button>
              <button className="btn btn-secondary" onClick={() => fileRef.current?.click()}>Import JSON</button>
              <input
                ref={fileRef}
                type="file"
                accept="application/json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) importData(f)
                  e.target.value = ''
                }}
              />
            </div>
          </SubSection>

          <SubSection title="Danger zone">
            <button className="btn btn-danger" onClick={deleteAccount}>Delete this log</button>
          </SubSection>
        </div>
      )}
    </Modal>
  )
}
